import { API, APIClass, Storage, StorageClass } from "aws-amplify";
import env from "./lib/env";
import { AuthService } from "./lib/auth";

/**
 * Nolaノベル AppSync 用の専用 API インスタンス。
 * グローバルな `API` (Amplify の singleton) とは独立した設定を保持するため、
 * Nola と Nolaノベル で同時に GraphQL 呼び出しが走っても
 * 認証タイプ (AWS_IAM / AMAZON_COGNITO_USER_POOLS) が取り違わらない。
 */
export const nolaNovelApi = new APIClass({});

/**
 * Nolaノベル S3 バケット用の専用 Storage インスタンス。
 * グローバルな `Storage` (Amplify の singleton) とは独立した設定を保持するため、
 * Nolaノベル の画像アクセス後もグローバル `Storage` のバケット設定 (Nola) が
 * 書き換わらず、`src/lib/s3.ts` 等の Nola バケット前提の Storage 操作に影響しない。
 */
export const nolaNovelStorage = new StorageClass();

// `nolaNovelApi` / `nolaNovelStorage` のエンドポイントとバケットは環境変数に依る静的値なので、
// モジュール読み込み時に一度だけ configure しておく。これにより `nolaNovelStorage.get()` 等が
// いつ呼ばれても `AWSS3Provider` が登録済みの状態となり、`NolaNovelApiInitializer.init()` を
// 事前に呼び出す必要がなくなる。呼び出しごとに変わり得る設定 (`clientMetadata` など) は
// `NolaNovelApiInitializer.init()` 側で必要時に設定する。
const nolaNovelEndpoint = env.VUE_APP_NOLANOVEL_API_ENDPOINT;
nolaNovelApi.configure({
  graphql_endpoint: `${nolaNovelEndpoint}/graphql`,
  graphql_endpoint_iam_region: "ap-northeast-1",
  aws_appsync_authenticationType: "AMAZON_COGNITO_USER_POOLS",
  endpoints: [
    {
      name: "GW",
      endpoint: `${nolaNovelEndpoint}/graphql`,
      region: "ap-northeast-1",
    },
    {
      name: "Rest",
      endpoint: nolaNovelEndpoint,
      region: "ap-northeast-1",
    },
  ],
  graphql_headers: async () => ({}),
});
nolaNovelStorage.configure({
  bucket: process.env.VUE_APP_NOLANOVEL_DATA_BUCKET, // REQUIRED -  Amazon S3 bucket
  region: "ap-northeast-1",
});

export class NolaApiInitializer {
  static async init(headers?: Record<string, string>): Promise<void> {
    // //////////
    // Amplify
    // //////////
    const service = new AuthService();
    service.configure({
      region: "ap-northeast-1",
      identityPoolId: env.VUE_APP_ID_POOL_ID!,
      userPoolId: env.VUE_APP_USER_POOL_ID!,
      userPoolWebClientId: env.VUE_APP_USER_POOL_CLIENT!,
      authenticationFlowType: "USER_SRP_AUTH",
      clientMetadata: { serviceName: "Nola" },
    });

    const endpoint = env.VUE_APP_API_ENDPOINT;
    const nolaContestEndpoint = env.VUE_APP_NOLACONTEST_API_ENDPOINT;
    const nolaPfContestEndpoint = env.VUE_APP_NOLAPFCONTEST_API_ENDPOINT;
    const writerTypeIndicatorEndpoint = `${env.VUE_APP_NOLA_WEB}/writer-type-indicator/api`;
    const nolaWebApiEndpoint = env.VUE_APP_NOLA_WEB_API_ENDPOINT;

    API.configure({
      graphql_endpoint: `${endpoint}/graphql`,
      graphql_endpoint_iam_region: "ap-northeast-1",
      aws_appsync_authenticationType: "AWS_IAM",

      endpoints: [
        {
          name: "GW",
          endpoint: `${endpoint}/graphql`,
          region: "ap-northeast-1",
        },
        {
          name: "Rest",
          endpoint,
          region: "ap-northeast-1",
        },
        {
          name: "NolaContestAPI",
          endpoint: nolaContestEndpoint,
          region: "ap-northeast-1",
        },
        {
          name: "WriterTypeIndicatorAPI",
          endpoint: writerTypeIndicatorEndpoint,
          region: "ap-northeast-1",
        },
        {
          name: "NolaRestAPI",
          endpoint: nolaWebApiEndpoint,
          region: "ap-northeast-1",
          Auth: {
            identityPoolId: env.VUE_APP_ID_POOL_ID!,
            region: "ap-northeast-1",
            userPoolId: env.VUE_APP_USER_POOL_ID!,
            userPoolWebClientId: env.VUE_APP_USER_POOL_CLIENT!,
          },
        },
        {
          name: "NolaPfContestAPI",
          endpoint: nolaPfContestEndpoint,
          region: "ap-northeast-1",
          Auth: {
            identityPoolId: env.VUE_APP_ID_POOL_ID!,
            region: "ap-northeast-1",
            userPoolId: env.VUE_APP_USER_POOL_ID!,
            userPoolWebClientId: env.VUE_APP_USER_POOL_CLIENT!,
          },
        },
      ],
      graphql_headers: async () => headers || {}, // 空オブジェクトを渡す場合は、ヘッダーを追加しない
    });

    Storage.configure({
      bucket: process.env.VUE_APP_DATA_BUCKET, // REQUIRED -  Amazon S3 bucket
      region: "ap-northeast-1",
    });
  }
}

/**
 * Nolaノベル 呼び出し用のグローバル状態 (`AuthService` の `clientMetadata.serviceName`) を
 * Nolaノベル 向けに切り替える。
 *
 * `clientMetadata` は Cognito の `CustomMessage` Lambda Trigger (サインアップ /
 * パスワードリセット等のメール) に渡される値で、Nola / Nolaノベル のメール文面を
 * サーバー側で振り分けるために使われる。
 *
 * AppSync 用の `nolaNovelApi` / S3 用の `nolaNovelStorage` の configure は
 * モジュール読み込み時に完了しているため、ここではトークン/画像取得側の
 * 配線には触らない。
 */
export class NolaNovelApiInitializer {
  static init(): void {
    const service = new AuthService();
    service.configure({
      region: "ap-northeast-1",
      identityPoolId: process.env.VUE_APP_ID_POOL_ID!,
      userPoolId: process.env.VUE_APP_USER_POOL_ID!,
      userPoolWebClientId: process.env.VUE_APP_USER_POOL_CLIENT!,
      clientMetadata: { serviceName: "Nolaノベル" },
    });
  }
}
